import React, { useContext, useEffect, useMemo } from 'react'
import { isEmpty } from 'lodash'

import Widget from '@/components/widget/Widget'
import WidgetContext from '@/components/widget/widget.context'
import WidgetContainer from '@/components/widget/WidgetContainer'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'

import { formatChannelLabel } from './helpers'

const formatNumber = (value) => Number(value ?? 0).toLocaleString()

const TOTAL_ROW_ID = '__total__'

const UsageByChannelTable = ({ byChannel, total, loading }) => {
  const { handleChangeCsvData } = useContext(WidgetContext)

  const hasData = !isEmpty(byChannel)

  // The Total row is appended only when there is real channel data — otherwise
  // the widget renders WidgetContainer's NoData state. `isTotal` flags the
  // total row so AdvancedTable can keep it pinned at the bottom regardless
  // of column sort (see `pinned` + `pinnedReverse` below).
  const rows = useMemo(() => {
    if (!hasData) {
      return []
    }
    const channelRows = byChannel.map((row) => ({
      id: row.channel,
      channel: formatChannelLabel(row.channel),
      interactions: row.interactions,
      isTotal: false,
    }))
    return [
      ...channelRows,
      {
        id: TOTAL_ROW_ID,
        channel: 'Total',
        interactions: total ?? 0,
        isTotal: true,
        // Tag the <tr> via `rowProps` spread (AdvancedTableBody.js:133). The
        // tr's `className` is rebuilt after the spread so a className prop
        // here would be dropped, but `data-*` attributes survive — see the
        // `[data-row-total]` rules in OmnichannelUsagePage.scss.
        rowProps: { 'data-row-total': '' },
      },
    ]
  }, [byChannel, total, hasData])

  const columns = useMemo(
    () => [
      { accessor: 'channel', label: 'Channel', isSearchable: false },
      {
        accessor: 'interactions',
        label: 'Interactions',
        format: formatNumber,
        headerAlignment: 'right',
      },
    ],
    []
  )

  // Match existing widget pattern (see UsersWithNoCallsWidget.js): only depend
  // on `rows`. `handleChangeCsvData` comes from a context whose identity
  // changes per render, so including it would loop forever.
  //
  // Drop the synthetic Total row from the CSV — it's a derived sum that's
  // useful in the rendered table (pinned at the bottom) but surprising in
  // an exported file where users expect raw per-channel records they can
  // pivot/sum themselves.
  useEffect(() => {
    const csvRows = rows.filter((row) => !row.isTotal)
    handleChangeCsvData({ headers: columns, data: csvRows })
  }, [rows])

  return (
    <WidgetContainer
      autoHeight={!loading && hasData}
      loading={loading}
      noData={!hasData}
      table={
        <AdvancedTable
          testid="omnichannel-usage-table"
          index="id"
          columns={columns}
          rows={rows}
          defaultOrderBy="interactions"
          defaultOrder="desc"
          pinned="isTotal"
          pinnedReverse
        />
      }
      chart={<div />}
    />
  )
}

/**
 * @param {string} [contractWindow] Pre-formatted contract year the counts cover,
 *   shown where the date picker used to be. The window is not selectable: the
 *   table is pinned to the same period as the headline so the two agree
 *   (QCER-1104).
 */
export const UsageByChannelWidget = ({ byChannel, total, loading, contractWindow }) => {
  return (
    <Widget
      label="Usage by Channel"
      subtitle="Interaction counts broken down by channel type."
      tableOnly
      showCsvDownload
      customControls={
        // Hidden while loading, matching the progress card's footer: until the
        // new response lands this still holds the previous organization's
        // window, and a label naming the wrong period beside a spinner is
        // exactly the mismatch this ticket set out to remove.
        contractWindow && !loading ? (
          <span className="omnichannel-usage__window-label" data-testid="omnichannel-usage-window">
            {contractWindow}
          </span>
        ) : null
      }
    >
      <UsageByChannelTable byChannel={byChannel} total={total} loading={loading} />
    </Widget>
  )
}
